import React, { useState, useEffect, useMemo, useRef } from 'react';
import {Server} from "./Server";
import {createPortal} from "react-dom";
import {motion, AnimatePresence} from 'framer-motion';
import UserStore from "../../store/user-store";
import { ServerFilter } from "./ServerFilter";

/**
 * @typedef {Object} Server
 * @property {string} server_id
 * @property {string} server_name
 */

/**
 * @param {Object} props
 * @param {Server[]} props.servers
 * @param {number} props.activeServerId
 * @param {(show: boolean) => void} props.setShowServerSelector
 * @param {(server: Server) => void} props.setActiveServer
 */
export const ServerSelector = ({servers, activeServerId, setShowServerSelector, setActiveServer}) => {
    const sortedServers = useMemo(() => 
        [...servers].sort((a, b) => a.server_name.localeCompare(b.server_name))
    , [servers]);
    
    const [filteredServers, setFilteredServers] = useState(sortedServers);
    const isFirstRender = useRef(true);

    useEffect(() => {
        if (isFirstRender.current) {
            isFirstRender.current = false;
            return;
        }
        setFilteredServers(sortedServers);
    }, [sortedServers]);

    const containerVariants = {
        hidden: {
            y: '100%',
            scale: 1.02,
            opacity: 0
        },
        visible: {
            y: 0,
            scale: 1,
            opacity: 1,
            transition: {
                duration: 0.5,
                ease: [0.22, 1, 0.36, 1]
            }
        },
        exit: {
            y: '100%',
            scale: 1.02,
            opacity: 0,
            transition: {
                duration: 0.3,
                ease: "easeOut"
            }
        }
    };
    const overlayVariants = {
        hidden: { opacity: 0 },
        visible: { 
            opacity: 0.5,
            transition: {
                duration: 0.25,
                ease: "easeOut"
            }
        },
        exit: { 
            opacity: 0,
            transition: {
                duration: 0.2,
                ease: "easeIn"
            }
        }
    };

    const langCode = UserStore.langCode;

    const serverList = useMemo(() => (
        filteredServers.length > 0 ? (
            <AnimatePresence mode="popLayout">
                {filteredServers.map((server, index) => (
                    <motion.div
                        key={server.server_id || index}
                        initial={isFirstRender.current ? undefined : { opacity: 0, y: 10 }}
                        animate={isFirstRender.current ? undefined : { opacity: 1, y: 0 }}
                        exit={isFirstRender.current ? undefined : { opacity: 0, y: 10 }}
                        transition={{
                            type: "tween",
                            duration: 0.3,
                            ease: [0.22, 1, 0.36, 1],
                            delay: isFirstRender.current ? 0 : index * 0.03,
                            exit: { delay: 0 }
                        }}
                        style={{
                            transform: 'translateZ(0)',
                            willChange: 'transform, opacity',
                        }}
                    >
                        <Server
                            server={server}
                            isBtn={false}
                            activeServerId={activeServerId}
                            setActiveServer={setActiveServer}
                        />
                    </motion.div>
                ))}
            </AnimatePresence>
        ) : (
            <motion.div 
                initial={isFirstRender.current ? undefined : { opacity: 0 }}
                animate={isFirstRender.current ? undefined : { opacity: 1 }}
                exit={isFirstRender.current ? undefined : { opacity: 0 }}
                transition={{ 
                    type: "tween",
                    duration: 0.3,
                    ease: [0.22, 1, 0.36, 1]
                }}
                style={{
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'rgba(255, 255, 255, 0.7)',
                    fontSize: '16px',
                    padding: '20px',
                    willChange: 'opacity'
                }}
            >
                {langCode === "ru" ? "Серверы не найдены" : "No servers found"}
            </motion.div>
        )
    ), [filteredServers, activeServerId, langCode, isFirstRender.current]);

    return createPortal(
        <>
            <motion.div
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={overlayVariants}
                style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    background: '#000',
                    zIndex: 99999,
                    willChange: 'opacity',
                }}
                onClick={() => setShowServerSelector(false)}
            />
            <motion.div
                layoutId="server-selector"
                initial="hidden"
                animate="visible"
                exit="exit"
                variants={containerVariants}
                style={{
                    backgroundColor: "#16181A",
                    position: "absolute",
                    bottom: 0,
                    width: "100%",
                    height: "90%",
                    padding: "0px 16px 20px 16px",
                    zIndex: 100000,
                    borderRadius: "30px 30px 0px 0px",
                    fontFamily: 'VK Sans Display, sans-serif',
                    willChange: 'transform',
                    display: "flex",
                    flexDirection: "column",
                    transform: 'translateZ(0)',
                    backfaceVisibility: 'hidden',
                    perspective: 1000,
                }}
            >
                <div style={{
                    display: "flex",
                    padding: "22px 0px 22px 0px",
                    position: 'relative',
                    alignItems: "center",
                    justifyContent: "center",
                }}>
                    <h1 style={{
                        fontSize: "24px",
                        lineHeight: "28px",
                        textAlign: "center",
                        margin: 0,
                    }}>{langCode === "ru" ? "Выбор сервера" : "Select server"}</h1>
                    <button style={{
                        position: "absolute",
                        right: 0,
                        padding: 15,
                        border: 'none',
                        borderRadius: "50%",
                        backgroundColor: "#2D2F31",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: 'pointer',
                    }} onClick={() => setShowServerSelector(false)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
                            <path fillRule="evenodd" clipRule="evenodd"
                                  d="M15.3332 2.39224L2.39199 15.3334L0.666504 13.6079L13.6077 0.666748L15.3332 2.39224Z"
                                  fill="white"/>
                            <path fillRule="evenodd" clipRule="evenodd"
                                  d="M13.6077 15.3334L0.666504 2.39224L2.39199 0.666748L15.3332 13.6079L13.6077 15.3334Z"
                                  fill="white"/>
                        </svg>
                    </button>
                </div>

                <ServerFilter
                    servers={sortedServers}
                    setFilteredServers={setFilteredServers}
                />

                <div style={{
                    border: "1px solid rgba(255, 255, 255, 0.15)",
                    borderRadius: 8,
                    flex: 1,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                }}>
                    {serverList}
                </div>
            </motion.div>
        </>,
        document.body
    );
};
